/**
 * Homepage secondary-stories carousel (Elements/newsstudios/FRONT_B.ctp).
 *
 * Two cards per page, moving a page (two) at a time, on the hero's clock:
 * `interval` and `speed` are FRONT_A's (lib/hero.js), so the two carousels
 * stacked in one column do not run at visibly different tempos. Keep them in
 * step if either changes.
 *
 * A plain `slide` type, not the hero's `fade`: two cards crossfading at once
 * reads as a flicker, and the fade's RTL translate workaround in
 * components.css is not needed for this type.
 */
import Splide from '@splidejs/splide';
import { qs, prefersReducedMotion } from './dom.js';
import { startAutoplayOnInput } from './autoplay.js';

export function setupFrontB() {
  const root = qs('[data-front-b]');
  if (!root) return;

  // One card needs no carousel — see hero.js for the same guard.
  if (root.querySelectorAll('.splide__slide').length < 2) return;

  const reduced = prefersReducedMotion();

  const splide = new Splide(root, {
    perPage: 2,
    // Matches the old grid's `gap-5`.
    gap: '1.25rem',
    rewind: true,
    // Read from the document, never hardcoded — see hero.js.
    direction: document.documentElement.getAttribute('dir') === 'ltr' ? 'ltr' : 'rtl',
    arrows: false,
    pagination: true,
    // Same start-on-first-input as the hero, so the two stay on one clock.
    autoplay: reduced ? false : 'pause',
    interval: 4000,
    speed: reduced ? 0 : 600,
    pauseOnHover: true,
    pauseOnFocus: true,
    // Below `sm` (tokens.css): one card per page, as the old grid was one
    // column there.
    breakpoints: {
      639: { perPage: 1 },
    },
    // No arrows and no autoplay toggle, so only the pagination and slide
    // labels are read. `slideX` is the dot label at one card per page
    // (below `sm`), `pageX` at two.
    i18n: {
      slideX: 'رفتن به اسلاید %s',
      pageX: 'رفتن به صفحه %s',
      carousel: 'اسلایدر خبرهای دوم',
      slide: 'اسلاید',
      slideLabel: 'اسلاید %s از %s',
      select: 'انتخاب اسلاید',
    },
  });

  splide.mount();
  if (!reduced) startAutoplayOnInput(root, splide);
  return splide;
}
